import { NotificationType } from "@/enums/notifications/NotificationType";
import { Environment } from "@/enums/system/Environment";
import { RouteNames } from "@/router/RouteNames";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { AuthenticationCookieManager } from "@/services/AuthenticationCookieManager";
import { LoginResponse } from "@/types/authorization/LoginResponse";
import { SignalRNotification } from "@/types/notifications/SignalRNotification";
import { getEnvironment } from "@/utils/system/EnvironmentReader";
import { Mutex } from "async-mutex";
import { defineStore } from "pinia";
import { computed, inject, ref } from "vue";
import { useRouter } from "vue-router";
import { useRemindersStore } from "./RemindersStore";

export const useNotificationsStore = defineStore('notificationsStore', () => {
    const connectionId = ref('');
    const notifications = ref<SignalRNotification[]>([]);

    const acquaintApiClient = inject('AcquaintApiClient') as AcquaintApiClient;
    const tokenCookie = ref<LoginResponse>();
    const router = useRouter();
    const remindersStore = useRemindersStore();

    const getConnectionId = () => connectionId.value;
    const setConnectionId = async (id: string) => {

        if (isUserLoggedIn() && id != connectionId.value) {
            await updateUserSignalRConnectionId(id);
        }

        connectionId.value = id;
    };

    const addNotification = async (notification: SignalRNotification) => {
        const mutex = new Mutex();

        // Must run this process exclusive to avoid race conditions if the same notification is sent multiple times
        await mutex.runExclusive(() => {
            /* Reminder refreshes need to be treated separately, 
             * as the notification is displayed differently depending what page the user is on */
            if (notification.type == NotificationType.RemindersRefreshed) {
                processRemindersNotification(notification);
                return;
            }

            /* Don't add the notification if it already exists
             * This will prevent users being notified multiple times about the same thing */
            if (notificationExists(notification)) {
                return;
            }

            notifications.value.push(notification);
        });
    }

    const notificationExists = (notification: SignalRNotification) => {
        return notifications.value.find(x => x.type == notification.type && x.linkId == notification.linkId);
    }

    const processRemindersNotification = (notification: SignalRNotification) => {
        if (router.currentRoute.value.name != RouteNames.Reminders && !notificationExists(notification)) {
            notifications.value.push(notification);
        }

        remindersStore.remindersRefreshing = false;
    }

    const getNotifications = () => notifications.value;

    const removeNotification = (notification: SignalRNotification) => {
        if (!notifications.value.includes(notification)) {
            return;
        }

        notifications.value = notifications.value.filter(x => x != notification);
    }

    const clearCache = () => {
        notifications.value = [];
    }

    const getHubUrl = () => {
        const domainName = window.location.host.includes('propertybutton.ie') ? 'propertybutton.ie' : 'acquaintcrm.co.uk';
        let subDomain = getEnvironment() === Environment.Production ? 'app' : 'apptest';

        const environment = getEnvironment();

        switch (environment) {
            case Environment.Development:
                subDomain = 'appdev';
                break;
            case Environment.Staging:
                subDomain = 'apptest';
                break;
            default:
                subDomain = 'app';
        }

        return `https://${subDomain}.${domainName}/notifications`;
    }

    /** Checks whether the user is currently logged in */
    const isUserLoggedIn = () => {
        const authenticationCookieManager = new AuthenticationCookieManager();
        tokenCookie.value = authenticationCookieManager.getDatabaseConnectorToken();

        return !!tokenCookie.value?.token;
    }

    const updateUserSignalRConnectionId = async (id: string) => {
        try {
            await acquaintApiClient.httpPutVoid(`database/PC/${tokenCookie.value?.pcid}`, { signalRConnectionId: id });
        } catch (err) {
            console.error(err);
        }
    }

    const notificationCount = computed(() => notifications.value.length);

    return { getConnectionId, setConnectionId, addNotification, getNotifications, removeNotification, getHubUrl, clearCache, notificationCount }
});